import { triggerPageExit } from "@tm/insurance/analytics/xdm";
import { navigationActions } from "@tm/insurance/navigation";
import { storePageTransitionStart } from "@tm/utility/analytics";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AppToast } from "./components/AppToast";
import { ComparePlansModal } from "./components/ComparePlansModal";
import { CompareToast } from "./components/CompareToast";
import type { PlanCardProps } from "./components/PlanCard/PlanCard";
import { COMPONENT_MAPPER } from "./constants/componentMapper";
import { useListingCompare } from "./hooks/useListingCompare";
import type {
  ListingPageV2Params,
  ListingPageV2Props,
  ListingV2Component,
  MyCashState,
} from "./ListingPageV2.types";
import {
  InnerFlex,
  ListContent,
  LoaderWrap,
  OverlayLoader,
  PageRoot,
  ScrollArea,
  Spinner,
} from "./Styles";
import { unwrapListingPayload } from "./utils/unwrapPayload";

type PlanBuyContext = Parameters<NonNullable<PlanCardProps["onBuyNow"]>>[0];

const SCROLL_THRESHOLD = 10;
const COMPARE_SUBTITLE_DISMISS_MS = 3000;

export const ListingPageV2 = ({
  payload,
  listingParams: listingParamsProp,
  onBack,
  onBuyNow,
  onTncOpen,
  isAnnualMultitrip = false,
  loading = false,
  className,
}: ListingPageV2Props) => {
  const data = useMemo(() => unwrapListingPayload(payload), [payload]);
  const components = data.components || [];
  const pageData = data.pageData;

  const [listingParams, setListingParams] = useState<ListingPageV2Params>(
    () => listingParamsProp || {}
  );
  useEffect(() => {
    if (listingParamsProp) setListingParams(listingParamsProp);
  }, [listingParamsProp]);

  const [myCashState, setMyCashState] = useState<MyCashState>("unApplied");
  const [showMyCashToast, setShowMyCashToast] = useState(false);
  const [showComparePlans, setShowComparePlans] = useState(false);
  const [showCompareSubtitle, setShowCompareSubtitle] = useState(false);
  const [scrollElevated, setScrollElevated] = useState(false);
  const [isNetPricing, setIsNetPricing] = useState(false);
  const [sortedQuoteIds, setSortedQuoteIds] = useState<string[] | null>(null);
  const [selectedPlanIds, setSelectedPlanIds] = useState<string[] | null>(null);
  const [contextNavLoading, setContextNavLoading] = useState(false);

  const {
    comparisonIds,
    showCompareToast,
    onComparePress,
    isPlanCompared,
    hideCompareToast,
  } = useListingCompare();

  useEffect(() => {
    hideCompareToast();
  }, [
    listingParams.countries,
    listingParams.startDate,
    listingParams.endDate,
    listingParams.travellers,
    hideCompareToast,
  ]);

  useEffect(() => {
    if (comparisonIds.size < 2) setShowCompareSubtitle(false);
  }, [comparisonIds]);

  useEffect(() => {
    if (!showCompareSubtitle) return;
    const t = window.setTimeout(
      () => setShowCompareSubtitle(false),
      COMPARE_SUBTITLE_DISMISS_MS
    );
    return () => clearTimeout(t);
  }, [showCompareSubtitle]);

  const handleCompareToggle = useCallback(
    (id: string, _selected: boolean) => {
      if (comparisonIds.size === 2 && !comparisonIds.has(id)) {
        setShowCompareSubtitle(true);
      }
      onComparePress(id);
    },
    [comparisonIds, onComparePress]
  );

  const contextEditComponent = components[0];
  const listComponents = components.slice(1);

  const compareNowWidget = pageData?.compareNowWidget;
  const compareSection = pageData?.comparePlansSection as
    | { title?: string }
    | undefined;

  const handleMyCashPress = useCallback(() => {
    setMyCashState((prev) => {
      const next = prev === "unApplied" ? "applied" : "unApplied";
      if (next === "applied") setShowMyCashToast(true);
      return next;
    });
  }, []);

  const handleSortApply = useCallback(
    (_sortKey: string, quoteIds: string[]) => {
      setSortedQuoteIds(quoteIds);
    },
    []
  );

  const handlePriceUpdate = useCallback((isNet: boolean) => {
    setIsNetPricing(isNet);
  }, []);

  const handlePlanSelect = useCallback((planIds: string[]) => {
    setSelectedPlanIds(planIds.length ? planIds : null);
  }, []);

  const handleContextBackPress = useCallback(() => {
    if (onBack) {
      onBack();
      return;
    }
    navigationActions.goBack();
  }, [onBack]);

  const handlePlanBuy = useCallback(
    (ctx: PlanBuyContext) => {
      if (onBuyNow) {
        onBuyNow({
          ...ctx,
          listingParams: { ...listingParams, myCashApplied: ctx.myCashApplied },
        });
        return;
      }
      if (typeof window === "undefined") return;
      const searchParams = new URLSearchParams(window.location.search);
      const queryParams = Object.fromEntries(searchParams.entries());
      const params = {
        ...queryParams,
        countries: listingParams.countries ?? searchParams.get("countries"),
        startDate: listingParams.startDate ?? searchParams.get("startDate"),
        endDate: listingParams.endDate ?? searchParams.get("endDate"),
        travellers: listingParams.travellers ?? searchParams.get("travellers"),
        isUpcomingTrip: searchParams.has("isUpcomingTrip")
          ? searchParams.get("isUpcomingTrip")
          : false,
        productId:
          ctx.productId !== undefined && ctx.productId !== null
            ? String(ctx.productId)
            : "",
        quoteId: ctx.quoteId,
      };
      triggerPageExit();
      storePageTransitionStart("listing");
      navigationActions.navigateToTravellerDetails(params);
    },
    [onBuyNow, listingParams]
  );

  const renderItem = useCallback(
    (item: ListingV2Component, index: number) => {
      const Cmp = COMPONENT_MAPPER[item.id];
      if (!Cmp) return null;

      const raw = (item.data || {}) as Record<string, unknown>;
      const k = `${item.id}-${index}`;

      if (item.id === "COMPONENT_LISTING_PAGE_CONTEXT_EDIT") {
        return (
          <Cmp
            key={k}
            {...raw}
            onConfirmNavigation={() => setContextNavLoading(true)}
            onBackPress={handleContextBackPress}
            onSortApply={handleSortApply}
            onPriceUpdate={handlePriceUpdate}
            onPlanSelect={handlePlanSelect}
            tripOption={listingParams.tripOption}
            planType={listingParams.planType}
            isEditExpanded={
              listingParams.isEditExpanded ?? pageData?.isEditExpanded
            }
            isAnnualMultitrip={isAnnualMultitrip}
            scrollElevated={scrollElevated}
          />
        );
      }

      if (item.id === "COMPONENT_LISTING_PAGE_MYP_MWEB_TRIP_CARDS") {
        return (
          <Cmp
            key={k}
            {...raw}
            onCompareToggle={handleCompareToggle}
            isPlanCompared={isPlanCompared}
            myCashState={myCashState}
            isNetPricing={isNetPricing}
            sortedQuoteIds={sortedQuoteIds}
            selectedPlanIds={selectedPlanIds}
            csrfToken={pageData?.["csrfToken"] as string | undefined}
            onBuyNow={handlePlanBuy}
            onTncOpen={onTncOpen}
          />
        );
      }

      if (item.id === "COMPONENT_LISTING_PAGE_MYCASH") {
        return (
          <Cmp
            key={k}
            {...raw}
            myCashState={myCashState}
            onMyCashPress={handleMyCashPress}
          />
        );
      }

      return <Cmp key={k} {...raw} />;
    },
    [
      handleContextBackPress,
      handleSortApply,
      handlePriceUpdate,
      handlePlanSelect,
      handleCompareToggle,
      isPlanCompared,
      handlePlanBuy,
      handleMyCashPress,
      listingParams,
      pageData,
      isAnnualMultitrip,
      scrollElevated,
      myCashState,
      isNetPricing,
      sortedQuoteIds,
      selectedPlanIds,
      onTncOpen,
    ]
  );

  const onScrollArea = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollElevated(e.currentTarget.scrollTop > SCROLL_THRESHOLD);
  }, []);

  const myCashToastText =
    (pageData?.toasts as { myCashApplied?: { text?: string } } | undefined)
      ?.myCashApplied?.text || "";
  const myCashToastIcon = (
    pageData?.toasts as { myCashApplied?: { icon?: string } } | undefined
  )?.myCashApplied?.icon;

  if (loading && !components.length) {
    return (
      <PageRoot className={className}>
        <LoaderWrap>
          <Spinner aria-label="Loading" />
        </LoaderWrap>
      </PageRoot>
    );
  }

  return (
    <PageRoot className={className}>
      <InnerFlex>
        <ScrollArea onScroll={onScrollArea}>
          {contextEditComponent ? renderItem(contextEditComponent, 0) : null}
          <ListContent>
            {listComponents.map((c, i) => renderItem(c, i + 1))}
          </ListContent>
        </ScrollArea>
      </InnerFlex>

      <CompareToast
        visible={Boolean(showCompareToast && compareNowWidget)}
        selectedCount={comparisonIds.size}
        compareButtonLabel={
          (compareNowWidget?.button as { title?: string } | undefined)?.title ||
          "Compare now"
        }
        toastSubtitle={compareNowWidget?.toast as string | undefined}
        showSubtitle={showCompareSubtitle}
        compareDisabled={comparisonIds.size !== 2}
        onCompare={() => {
          if (comparisonIds.size === 2) setShowComparePlans(true);
        }}
        onClose={() => {
          hideCompareToast();
          setShowCompareSubtitle(false);
        }}
      />

      <AppToast
        visible={showMyCashToast}
        message={myCashToastText}
        iconUrl={myCashToastIcon}
        onDone={() => setShowMyCashToast(false)}
      />

      <ComparePlansModal
        open={showComparePlans}
        onClose={() => setShowComparePlans(false)}
        title={compareSection?.title || "Compare plans"}
      />

      {(loading && components.length) || contextNavLoading ? (
        <OverlayLoader>
          <Spinner aria-label="Loading" />
        </OverlayLoader>
      ) : null}
    </PageRoot>
  );
};

export default ListingPageV2;
